"use client";

import { useEffect, useRef } from "react";

import {
  buildEcommerceEvent,
  createEcommercePayload,
  mapProductToItem,
  resolveEcommerceItemCategory,
} from "@/lib/analytics/ecommerce-analytics";
import { getMatchingItemListOrigin } from "@/lib/analytics/item-list-context";

import { EVENTS } from "@/constants/analytics.constants";
import { useTagging } from "@/hooks/useTagging";

export function PdpViewItem({ product }: PdpViewItemProps) {
  const [, setTag] = useTagging();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const origin = getMatchingItemListOrigin(product);
    const itemListId = origin?.item_list_id;
    const itemListName = origin?.item_list_name;

    const category = resolveEcommerceItemCategory(product);
    const mainItem = mapProductToItem(product, { index: 0, category });

    const items = [mainItem];

    if (product.includedProducts?.length) {
      product.includedProducts.forEach((included, idx) => {
        items.push(
          mapProductToItem(included, {
            index: idx + 1,
            category: resolveEcommerceItemCategory(included),
          }),
        );
      });
    }

    const payload = createEcommercePayload(items, {
      item_list_id: itemListId,
      item_list_name: itemListName,
    });

    setTag(buildEcommerceEvent(EVENTS.VIEW_ITEM, payload), { reset: true });
  }, [product, setTag]);

  return null;
}

type PdpProduct = {
  id?: string;
  slug?: string;
  name?: string;
  listPrice?: number | null;
  salesPrice?: number | null;
  type?: string;
  includedProducts?: Array<{
    id?: string;
    slug?: string;
    name?: string;
    listPrice?: number | null;
    salesPrice?: number | null;
    type?: string;
  }>;
};

type PdpViewItemProps = {
  product: PdpProduct;
};
